import React from 'react'

import { format } from "d3-format"
import { timeFormat } from "d3-time-format"
import { scaleTime } from "d3-scale"

import { ThemeStore } from '../stores/ThemeStore'
import { CoinStore } from '../stores/CoinStore'
import { DrawingState } from '../stores/DrawingState'

import {
  ChartCanvas, Chart,
  series, coordinates, tooltip, axes, indicator, helper,
  interactive
} from "react-stockcharts"

var { OHLCSeries, CandlestickSeries, BollingerSeries, BarSeries, StraightLine } = series;

var { CrossHairCursor, MouseCoordinateX, MouseCoordinateY } = coordinates;
var { EdgeIndicator } = coordinates;

var { OHLCTooltip } = tooltip;

var { XAxis, YAxis } = axes;
var { change, bollingerBand, heikinAshi } = indicator;

var { fitWidth } = helper;

var { TrendLine, ClickCallback } = interactive;


export class CryptoChart extends React.Component {
  state = {
    loaded: false,
    data: [],
    currentPrice: null, // refers to an object in data
    enableTrendLine: false,
    trendlineCount: 0,
    dateRange: null,
  }

  componentDidMount() {
    this.fetchData(this.props)

    // when coin store sends us ticker update, update chart and if needed
    // load more chart data
    this.handler = CoinStore.subscribe(this.props.symbol, (data) => {
      this.setCurrentPrice(data)
    })

    DrawingState.on('trendline', (action) => {
      if (action === 'on') {
        this.setState({enableTrendLine: true})
        document.addEventListener("keyup", this.onKeyPress)
      }
      else if (action === 'off') {
        this.setState({enableTrendLine: false})
        document.removeEventListener("keyup", this.onKeyPress)
      }
      else if (action === 'clear') {
        var count = this.state.trendlineCount
        while (count > 0) this.refs.trend.removeLast()
        this.setState({trendlineCount: 0})
      }
    })
  }

  componentWillUnmount() {
    CoinStore.unsubscribe(this.props.symbol, this.handler)
    DrawingState.removeListener('trendline')
  }

  componentWillReceiveProps(nextProps) {
    let {symbol, interval} = this.props

    if (nextProps.symbol !== symbol || nextProps.interval !== interval) {
      this.fetchData(nextProps)
      
      CoinStore.unsubscribe(this.props.symbol, this.handler)
      this.setState({currentPrice: null})

      CoinStore.subscribe(nextProps.symbol, (data) => {
        this.setCurrentPrice(data)
      })
    }
  }

  loadMore = (prices) => {
    var cp = this.state.currentPrice
    var interval = this.props.interval

    if (typeof prices !== 'object') {
      // DEBUG ONLY --
      prices = this.makeUpData(cp, interval)
    }

    // note the 100 future data points, plus 1 for the current data point
    var data = this.state.data.slice(0, this.state.data.length - 101)
    data = data.concat(prices)

    // new current price point
    cp = data[data.length - 1]

    // add back 100 future data points
    var lastTime = cp.date.getTime()
    var inc = interval * 1000
    for (var i = 0; i < 100; i++) {
      lastTime += inc // increment by interval first
      var date = new Date(lastTime)
      data.push({date})
    }

    var plotData = this.chartRef.getDataInfo().plotData
    var plotStart = plotData[0]
    var plotEnd = plotData[plotData.length - 1]
    var dateRange = [plotStart.date, plotEnd.date]

    // persist date range if the last one is not a *future* data point
    // meaning we only persist date range if *gutter* is not visible
    if (!plotEnd.close) dateRange = null

    this.setState({ data: data, currentPrice: cp, dateRange: dateRange })
  }

  setCurrentPrice = (price) => {
    let {close, date} = price
    let {dataSource, symbol, interval} = this.props
    var cp = this.state.currentPrice

    // check date does not exceed the current interval
    // if it exceeds, load more data and don't update price
    if (date.getTime() - cp.date.getTime() >= interval * 1000) {
      var start = cp.date
      var end = new Date()
      var params = { start, end, symbol, interval, dataSource}

      this.fetchData(params, prices => {
        this.loadMore(prices)
      })
      // return
      // continue to show the new price
    }

    cp.close = close
    if (cp.close < cp.low) {
      cp.low = cp.close
    }
    if (cp.close > cp.high) {
      cp.high = cp.close
    }
    this.setState({currentPrice: cp})
  }

  onTrendLineComplete = () => {
    this.setState({trendlineCount: this.state.trendlineCount+1})
  }

  onKeyPress = (e) => {
    var keyCode = e.which;
    // console.log(keyCode);
    switch (keyCode) {
      case 46: { // DEL
        this.refs.trend.removeLast();
        break;
      }
      case 27: { // ESC
        this.refs.trend.terminate();
        break;
      }
      default: break;
    }
  }

  plot = () => {
    var type = "hybrid";
    var { width, height, ratio, symbol, interval } = this.props
    var { data, currentPrice } = this.state

    // console.log('Plotting ' + data.length + ' price points')

    var margin = {left: 20, right: 80, top:20, bottom: 0}
    var gridHeight = height - margin.top - margin.bottom
    var gridWidth = width - margin.left - margin.right

    var padding = {top: 20, right: 0, bottom: 40, left: 0}

    var showGrid = true;
    var yGrid = showGrid ? { 
        innerTickSize: -1 * gridWidth,
        tickStrokeDasharray: 'Solid',
        tickStrokeOpacity: 0.1,
        tickStrokeWidth: 1
    } : {};
    var xGrid = showGrid ? { 
        innerTickSize: -1 * gridHeight,
        tickStrokeDasharray: 'Solid',
        tickStrokeOpacity: 0.1,
        tickStrokeWidth: 1
    } : {};
    
    var xrange = this.state.dateRange
    if (!xrange) {
      // display last 100 bars with 3 bars (in the future) as gutter
      var firstIdx = 0
      var first = data[0]
      if (data.length > 200) {
        firstIdx = data.length - 197 - 1
        first = data[firstIdx]
      }
      var last = data[firstIdx + 100]
      xrange = [first.date, last.date]
    }

    // display formats
    var priceFormat = ".8f"
    if (data[0].close > 1) priceFormat = ".4f"
    var unit = "%d"
    if (interval < 14400) unit = "%H"

    var theme = ThemeStore.get()

    var bb = bollingerBand()
      .merge((d, c) => {d.bb = c})
      .accessor(d => d.bb)
      .stroke({top: theme.bb_stroke, middle: theme.bb_stroke, bottom: theme.bb_stroke})
      .fill(theme.bb_fill);

    var changeCalculator = change()

    var calculators = [changeCalculator, bb]
    if (ThemeStore.getChartType() === 'ha') {
      calculators = [heikinAshi(), bb]
    } else if (ThemeStore.getChartType() === 'candl') {
      calculators = [bb]
    }

    return (
      <ChartCanvas ratio={ratio} width={width} height={height}
          margin={margin} type={type}
          seriesName={symbol}
          data={data} calculator={calculators}
          xAccessor={d => d.date} xScale={scaleTime()}
          xExtents={xrange}
          drawMode={this.state.enableTrendLine}
          ref={(ref) => { this.chartRef = ref }}
        >
        
        <Chart id={1}
            yExtents={[d => d.volume]}
            height={70} origin={(w, h) => [0, h - (padding.bottom+50)]}>

          <BarSeries yAccessor={d => d.volume}
            fill={d => d.close > d.open ? theme.volume_green : theme.volume_red } />
        </Chart>

        <Chart id={2} height={height - padding.bottom}
            yExtents={d => [d.high, d.low]}
            padding={padding}>
          <YAxis axisAt="right" orient="right" ticks={5} stroke={theme.axis_stroke}
            tickFormat={format(priceFormat)} fontSize={11} tickStroke={theme.axis_stroke}
            {...yGrid} />

          <XAxis axisAt="bottom" orient="bottom" fontSize={11} stroke={theme.axis_stroke}
            tickFormat={timeFormat(unit)} tickStroke={theme.axis_stroke}
            {...xGrid}/>

          {currentPrice &&
            <StraightLine type="horizontal" yValue={currentPrice.close}
            opacity={0.5} strokeWidth={1}
            stroke={currentPrice.close > currentPrice.open ? theme.price_green : theme.price_red}/>
          }

          <MouseCoordinateX at="bottom" orient="bottom"
            rectWidth={120} fontSize={11}
            displayFormat={timeFormat("%Y-%m-%d %H:%M:%S")} />
          <MouseCoordinateY at="right" orient="right"
            rectWidth={70} fontSize={11}
            displayFormat={format(priceFormat)} />

          <BollingerSeries calculator={bb} />
          { ThemeStore.getChartType() === 'ohlc' &&
            <OHLCSeries stroke={d => d.close > d.open ? theme.price_green : theme.price_red}/>
          }
          { (ThemeStore.getChartType() === 'ha' || ThemeStore.getChartType() === 'candl') &&
            <CandlestickSeries
              opacity={1.0} widthRatio={0.5} candleStrokeWidth={1.0}
              // wickStroke = stroke
              fill={d => d.close > d.open ? theme.candle_green_fill : theme.candle_red_fill}
              stroke={d => d.close > d.open ? theme.candle_green_stroke : theme.candle_red_stroke}
              wickStroke={d => d.close > d.open ? theme.candle_green_stroke : theme.candle_red_stroke}
            />
          }
          <EdgeIndicator itemType="last" orient="right" edgeAt="right"
            displayFormat={format(priceFormat)} rectWidth={70} hideLine={true}
            yAccessor={d => d.close} fontSize={11}
            textFill={d => {
              var val = d.close > d.open ? theme.edge_green_text : theme.edge_red_text
              if (!val) val = "#ffffff"
              return val
            }}
            fill={d => d.close > d.open ? theme.edge_green : theme.edge_red}/>

          <OHLCTooltip origin={[-10, -5]} showDate={false}
            ohlcFormat={format(priceFormat)} />

          <ClickCallback enabled={true}
            onClick={(e) => this.props.onClick(e.xy)}/>
        </Chart>

        <Chart id={3} height={310}
          yExtents={d => [d.high, d.low]}
          padding={{ top: 10, right: 0, bottom: 10, left: 0 }}>

        {/* FIXME: how to make this extend beyond current x range? */}
          <TrendLine ref="trend"
            enabled={this.state.enableTrendLine}
            type="LINE"
            snap={false}
            stroke={theme.trendline}
            strokeWidth={1}
            onStart={() => {
              // TODO: show color picker
            }}
            onComplete={this.onTrendLineComplete}
          />
        </Chart>

        <CrossHairCursor stroke={theme.crosshair} />
      </ChartCanvas>
    )
  }

  prettify(symbol) {
    const [a, b] = symbol.split('_')
    return b + a
  }

  render() {
    if (!this.state.loaded) return (
      <div className="ml2 mb2">
        Loading...
      </div>
    )

    return (
      <div>
        <p className='pl2 ma0'>
          <button className='bg-animate f6 di pv0 ph1 mr1' onClick={this.props.onClose}>X</button>
          { this.prettify(this.props.symbol) }
        </p>
        <div className='noselect'>
          { this.plot() }
        </div>
      </div>
    )
  }

  setData = (prices) => {
    if (prices.length === 0) return

    let {interval} = this.props
    var currentPrice = prices[prices.length - 1]

    // add 100 more data points in the future
    var lastTime = currentPrice.date.getTime()
    var inc = interval * 1000
    for (var i = 0; i < 100; i++) {
      lastTime += inc // increment by interval first
      var date = new Date(lastTime)
      prices.push({date})
    }

    var loaded = true
    var data = prices
    this.setState({ loaded, data, currentPrice })
  }

  fetchData = (props, fn) => {
    let {dataSource} = props
    dataSource(props)
      .then(rep => rep.json())
      .then(json => {

        var prices = json.map(d => {
          var p = {}
          p.date = new Date(d.date * 1000);
          p.open = d.open;
          p.high = d.high;
          p.low = d.low;
          p.close = d.close;
          p.volume = d.volume;

          return p;
        })

        if (typeof fn !== 'undefined') {
          fn(prices)
        } else {
          this.setData(prices)
        }

      })
      .catch(err => {
        // try again
        setTimeout(() => {
          this.fetchData(props)
        }, 300)
      })
  }

  // DEBUG ONLY ---
  makeUpData(cp, interval) {
    var prices = []
    var lastTime = cp.date.getTime()
    for (var i = 0; i < 2; i++) {
      var date = new Date(lastTime)
      var open = cp.close // continue from previous close
      var change = Math.random() / 1000000
      var dir = ( Math.round(Math.random() * 10) % 2 === 0 ? 1 : -1)
      prices.push({
        date: date,
        open: open,
        low: open   - change,
        high: open  + change,
        close: open + change * dir * Math.random(),
        volume: Math.random() * 100
      })
      lastTime += interval * 1000
    }
    return prices
  }
}

export default fitWidth(CryptoChart)


// WEBPACK FOOTER //
// ./src/components/CryptoChart.js